import { FC, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router";
import "./index.css";
import { drawerWidth } from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";
import { ReactComponent as CameraIcon } from "../../../assets/images/camera-01.svg";
import { ReactComponent as CoinSvg } from "../../../assets/images/coin.svg";
import { errorToast } from "../../../utils/toast";
import {
  userApi,
  useUploadProfilePictureMutation,
} from "../../../services/user";
import { setUser } from "../../../rtkSlices/metaDataSlice";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import UserInfoTab from "./UserInfo";
import { useSearchParams } from "react-router-dom";
import { SubscriptionDialog } from "../../../components/SubscriptionDialog";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const UserProfile: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const metaData = useSelector((state: any) => state.metaData);
  const { firstName, lastName } = metaData.userInfo;
  const username = metaData.userInfo["cognito:username"];
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedProfilePic, setSelectedProfilePic] = useState("");
  const [uploadProfilePicture, uploadProfilePictureResult] =
    useUploadProfilePictureMutation();
  const profile_picture = metaData.userInfo.profile_picture;
  const [confirmationProps, setConfirmationProps] = useState<any>({
    title: "",
    content: "",
    btn1: "",
    btn2: "",
    handleBtn1: () => {},
    handleBtn2: () => {},
    icon: <div></div>,
    tooltip: "",
  });
  const [value, setValue] = useState(0);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [subscriptionProps, setSubscriptionProps] = useState<any>({
    title: "",
    content: "",
    btn1: "",
    handleBtn1: () => {},
    icon: <div></div>,
    bgimage: false,
    airhorn: false,
    height: "402px",
    width: "",
    subContent: "",
    cancel: () => {},
  });
  let tempSubscriptionProps: {
    title: string;
    content: string | JSX.Element;
    btn1: string;
    handleBtn1: () => void;
    bgimage: boolean;
    icon: JSX.Element;
    airhorn: boolean;
    height: string;
    btncolor: string;
    subContent: string;
    cancel: (() => void) | undefined;
    cancelText: string;
    width: string;
  } = {
    title: "",
    content: "",
    btn1: "",
    handleBtn1: () => {},
    bgimage: false,
    icon: <div></div>,
    airhorn: false,
    height: "402px",
    btncolor: "#218F54",
    subContent: "",
    cancel: undefined,
    cancelText: "Later",
    width: "auto",
  };
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const defaultDialogProps = () => {
    return {
      title: "",
      content: "",
      btn1: "",
      btn2: "",
      handleBtn1: () => {},
      handleBtn2: () => {},
      actions: true,
      icon: <div></div>,
    };
  };
  let tempConfirmationProps = defaultDialogProps();
  type SubscriptionProps = {
    title: string;
    content: string | JSX.Element;
    btn1: string;
    handleBtn1: () => void;
    bgimage: boolean;
    icon: JSX.Element;
    airhorn: boolean;
    height: string;
    btncolor: string;
    subContent: string;
    cancel: (() => void) | undefined;
    cancelText: string;
    width: string;
  };

  const enableSubscriptionPopup = ({
    title,
    content,
    btn1,
    handleBtn1,
    bgimage,
    icon,
    airhorn,
    height,
    btncolor,
    subContent,
    cancel,
    cancelText,
    width,
  }: SubscriptionProps) => {
    setShowSubscriptionPopup(true);
    tempSubscriptionProps = {
      title,
      content,
      icon,
      bgimage,
      btn1,
      airhorn,
      btncolor,
      handleBtn1,
      height,
      width,
      cancel,
      subContent,
      cancelText,
    };
    setSubscriptionProps(tempSubscriptionProps);
  };

  useEffect(() => {
    const return_from = searchParams.get("return_from");
    if (return_from) {
      setShowSubscriptionPopup(true);
      const popupProps = {
        title:
          return_from == "alphaplan"
            ? "Welcome to Alpha!"
            : "You've got more tokens",
        content:
          return_from == "alphaplan" ? (
            <div>
              You’re all set with <b>20,000</b> tokens and full access to HiiBo!
            </div>
          ) : return_from.includes("pack") ? (
            <div>
              You’re all set with{" "}
              <b>
                {return_from == "smallpack"
                  ? "10,000"
                  : return_from == "mediumpack"
                  ? "40,000"
                  : "100,000"}
              </b>{" "}
              more tokens
            </div>
          ) : (
            <></>
          ),
        icon: <></>,
        bgimage: true,
        btn1: return_from == "alphaplan" ? "Start Chatting" : "Keep Chatting",
        airhorn: true,
        btncolor: "#218F54",
        handleBtn1: () => navigate("/chat"),
        height: "auto",
        width: "auto",
        cancel: () => setShowSubscriptionPopup(false),
        subContent: "",
        cancelText: "",
      };
      enableSubscriptionPopup(popupProps);
    }
  }, [searchParams.get("return_from")]);

  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: "#2a945f",
      },
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }

  function getExtension(filename: string) {
    return filename.split(".").pop();
  }

  const updateProfilePicture = async (file: string | Blob) => {
    const formData = new FormData();
    formData.append("file", file);
    const response: any = await uploadProfilePicture({
      body: formData,
      params: `?username=${username}`,
    });
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
      setSelectedProfilePic("");
    } else {
      dispatch(setUser({ profile_picture: response.data.imageUrl }));
    }
  };

  const selectProfilePic = (file: any) => {
    const ext = getExtension(file.name);
    if (ext == "jpg" || ext == "png" || ext == "svg" || ext == "jpeg") {
      setSelectedProfilePic(URL.createObjectURL(file));
      updateProfilePicture(file);
    } else {
      errorToast("Invalid file type");
    }
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
        style={{ width: "100%" }}
      >
        {value === index && (
          <Box sx={{ padding: "0px 10px", width: "100%" }}>{children}</Box>
        )}
      </div>
    );
  }

  return (
    <div>
      <Box
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          marginTop: "1%",
          padding: "0px 20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="user-profile-pic">
          <Avatar
            {...stringAvatar(`${firstName} ${lastName}`)}
            sx={{
              textTransform: "uppercase",
              fontWeight: "600",
              width: "143px",
              height: "143px",
              fontSize: "52px",
              backgroundColor: "#2a945f",
              opacity: uploadProfilePictureResult.isLoading ? "0.5" : "1",
            }}
            src={selectedProfilePic || profile_picture || ""}
          />
          {uploadProfilePictureResult.isLoading ? (
            <div className="profilepic-loader">
              <CircularProgress />
            </div>
          ) : null}
          <CameraIcon
            className="camera-icon"
            onClick={() => document.getElementById("profilepic-file")?.click()}
          />
          <input
            type="file"
            id="profilepic-file"
            onChange={(e) => {
              if (e.target && e.target.files) {
                selectProfilePic(e.target.files[0]);
              }
            }}
          />
          <Typography
            sx={{
              fontWeight: "600",
              textTransform: "capitalize",
              marginTop: "10px",
              textAlign: "center",
            }}
          >{`${firstName} ${lastName}`}</Typography>
        </div>
        <CustomTabPanel value={value} index={0}>
          <UserInfoTab />
        </CustomTabPanel>
      </Box>
      {showConfirmation ? (
        <ConfirmationDialog
          open={showConfirmation}
          content={confirmationProps.content}
          title={confirmationProps.title}
          btn1={confirmationProps.btn1}
          btn2={confirmationProps.btn2}
          handleBtn1={confirmationProps.handleBtn1}
          handleBtn2={confirmationProps.handleBtn2}
          icon={confirmationProps.icon}
          actions={confirmationProps.actions}
          tooltip={confirmationProps.tooltip}
        />
      ) : null}
      {showSubscriptionPopup ? (
        <SubscriptionDialog
          open={showSubscriptionPopup}
          content={subscriptionProps.content}
          title={subscriptionProps.title}
          btn1={subscriptionProps.btn1}
          handleBtn1={subscriptionProps.handleBtn1}
          icon={subscriptionProps.icon}
          bgimage={subscriptionProps.bgimage}
          btncolor={subscriptionProps.btncolor}
          cancel={subscriptionProps.cancel}
          airhorn={subscriptionProps.airhorn}
          height={subscriptionProps.height}
          width={subscriptionProps.width}
          subContent={subscriptionProps.subContent}
          cancelText={subscriptionProps.cancelText}
        />
      ) : null}
    </div>
  );
};

export default UserProfile;
